import { Injectable, computed, inject, signal } from '@angular/core';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserCacheLocation,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type RedirectRequest,
  type SilentRequest
} from '@azure/msal-browser';

import { ENTRA_AUTH_CONFIG } from './entra-auth.config';

@Injectable({ providedIn: 'root' })
export class EntraAuthService {
  private readonly config = inject(ENTRA_AUTH_CONFIG);
  private readonly msal = this.config.enabled
    ? new PublicClientApplication({
        auth: {
          clientId: this.config.clientId,
          authority: this.config.authority,
          knownAuthorities: [...this.config.knownAuthorities],
          redirectUri: this.config.redirectUri,
          postLogoutRedirectUri: this.config.postLogoutRedirectUri
        },
        cache: {
          cacheLocation: BrowserCacheLocation.SessionStorage
        }
      })
    : null;

  readonly initialized = signal(!this.config.enabled);
  readonly isBusy = signal(false);
  readonly account = signal<AccountInfo | null>(null);
  readonly lastError = signal<string | null>(null);
  readonly isAuthenticated = computed(() => this.account() !== null);
  readonly displayName = computed(() => this.account()?.name ?? this.account()?.username ?? 'Guest');
  readonly username = computed(() => this.account()?.username ?? null);

  /**
   * Returns whether the current user is a member of the "MKZS - Portal Admin" group.
   * Reads the "groups" claim from the MSAL account's idTokenClaims.
   *
   * When groupMembershipClaims is set on the app registration, the ID token contains
   * a "groups" array of group Object IDs. This method checks if the admin group ID
   * is present in that array.
   *
   * Returns true when auth is disabled, consistent with the existing guard behaviour.
   * Returns false when the groups claim is absent or does not include the admin group.
   * Never throws.
   */
  isPortalAdmin(): boolean {
    if (!this.config.enabled) {
      return true;
    }

    const adminGroupId = this.config.portalAdminGroupId;
    if (!adminGroupId) {
      return false;
    }

    const acct = this.account();
    if (!acct) {
      return false;
    }

    const groups = acct.idTokenClaims?.['groups'];
    if (!Array.isArray(groups)) {
      return false;
    }

    return groups.includes(adminGroupId);
  }

  async initialize(): Promise<void> {
    if (!this.msal || this.initialized()) {
      return;
    }

    this.isBusy.set(true);
    this.lastError.set(null);

    try {
      await this.msal.initialize();
      const redirectResult = await this.msal.handleRedirectPromise();
      this.captureAuthenticationResult(redirectResult);

      if (!redirectResult) {
        const activeAccount = this.msal.getActiveAccount() ?? this.msal.getAllAccounts()[0] ?? null;
        if (activeAccount) {
          this.msal.setActiveAccount(activeAccount);
        }
        this.account.set(activeAccount);
      }
    } catch (error) {
      this.lastError.set(this.toErrorMessage(error));
    } finally {
      this.initialized.set(true);
      this.isBusy.set(false);
    }
  }

  async signIn(): Promise<void> {
    if (!this.msal) {
      return;
    }

    await this.initialize();
    this.lastError.set(null);

    const request: RedirectRequest = {
      scopes: [...this.config.apiScopes],
      redirectStartPage: window.location.href
    };

    await this.msal.loginRedirect(request);
  }

  async signOut(): Promise<void> {
    if (!this.msal) {
      return;
    }

    await this.initialize();

    await this.msal.logoutRedirect({
      account: this.account() ?? undefined,
      postLogoutRedirectUri: this.config.postLogoutRedirectUri
    });
  }

  async acquireApiAccessToken(): Promise<string> {
    if (!this.msal) {
      throw new Error('Microsoft Entra External ID is disabled for this environment.');
    }

    await this.initialize();

    const account = this.account();
    if (!account) {
      throw new Error('A signed-in account is required before calling the protected API.');
    }

    const request: SilentRequest = {
      account,
      scopes: [...this.config.apiScopes]
    };

    try {
      const result = await this.msal.acquireTokenSilent(request);
      this.captureAuthenticationResult(result);
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        this.lastError.set('Interactive sign-in is required before the API token can be acquired.');
        await this.msal.acquireTokenRedirect({
          account,
          scopes: [...this.config.apiScopes],
          redirectStartPage: window.location.href
        });
        return '';
      }

      const message = this.toErrorMessage(error);
      this.lastError.set(message);
      throw new Error(message);
    }
  }

  private captureAuthenticationResult(result: AuthenticationResult | null): void {
    const nextAccount = result?.account ?? null;
    if (!nextAccount || !this.msal) {
      return;
    }

    this.msal.setActiveAccount(nextAccount);
    this.account.set(nextAccount);
  }

  private toErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'An unexpected Microsoft Entra authentication error occurred.';
  }
}
